import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { gql } from 'graphql-request'
import { Loader2, Smile, ChevronRight } from 'lucide-react'
import { useStores } from '@/components/StoreProvider'
import { Button } from '@/components/Common/Button'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import Layout from '@/components/Layout/Layout'
import Logo from '@/images/logo.svg?react'
import User from '@/images/user.svg?react'

const DEFAULT_VIDEO_ID_1 = 'Q7P2TCfGSFw'
const DEFAULT_VIDEO_ID_2 = 'QcvKTAAI41A'

const GET_ONBOARDING_VIDEOS = gql`
  query getOrganisationOnboardingVideos($organisationId: Int!) {
    getOrganisationOnboardingVideos(organisationId: $organisationId) {
      onboardingVideo1Url
      onboardingVideo2Url
    }
  }
`

function extractYouTubeId(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.hostname === 'youtu.be' || parsed.hostname === 'www.youtu.be') {
      return parsed.pathname.slice(1).split('/')[0] || null
    }
    if (parsed.pathname.includes('/embed/')) {
      return parsed.pathname.split('/embed/')[1]?.split('/')[0] || null
    }
    return parsed.searchParams.get('v') || null
  } catch {
    return null
  }
}

const Welcome: React.FC = observer(() => {
  const { userStore, settingsStore } = useStores()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const player1Ref = useRef<any>(null)
  const player2Ref = useRef<any>(null)
  const [hasWatchedVideo1, setHasWatchedVideo1] = useState(false)
  const [hasWatchedVideo2, setHasWatchedVideo2] = useState(false)
  const [player1Ready, setPlayer1Ready] = useState(false)
  const [player2Ready, setPlayer2Ready] = useState(false)
  const [currentStep, setCurrentStep] = useState<'getting-started' | 'video-tutorial'>('getting-started')
  const [videoId1, setVideoId1] = useState(DEFAULT_VIDEO_ID_1)
  const [videoId2, setVideoId2] = useState(DEFAULT_VIDEO_ID_2)
  const [videosLoaded, setVideosLoaded] = useState(false)

  useEffect(() => {
    // Check if user is authenticated
    if (!userStore.isAuthenticated) {
      navigate('/auth')
    }
  }, [userStore.isAuthenticated, navigate])

  // Fetch org-specific video URLs
  useEffect(() => {
    const orgId = userStore.user?.organization_id
    if (!orgId) {
      setVideosLoaded(true)
      return
    }

    authorizedGraphQLRequest<{ getOrganisationOnboardingVideos: { onboardingVideo1Url: string | null; onboardingVideo2Url: string | null } }>(GET_ONBOARDING_VIDEOS, { organisationId: orgId })
      .then((data) => {
        const { onboardingVideo1Url, onboardingVideo2Url } = data.getOrganisationOnboardingVideos || {}
        if (onboardingVideo1Url) {
          const id = extractYouTubeId(onboardingVideo1Url)
          if (id) setVideoId1(id)
        }
        if (onboardingVideo2Url) {
          const id = extractYouTubeId(onboardingVideo2Url)
          if (id) setVideoId2(id)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch onboarding videos, using defaults:', err)
      })
      .finally(() => {
        setVideosLoaded(true)
      })
  }, [userStore.user?.organization_id])

  useEffect(() => {
    if (!videosLoaded) return

    // Load YouTube IFrame API
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    const firstScriptTag = document.getElementsByTagName('script')[0]
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)

    // YouTube API ready callback
    ;(window as any).onYouTubeIframeAPIReady = () => {
      // Initialize both players (they exist in DOM from the start now)
      player1Ref.current = new (window as any).YT.Player('youtube-player-1', {
        height: '100%',
        width: '100%',
        videoId: videoId1,
        playerVars: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: onPlayer1Ready,
          onStateChange: onPlayer1StateChange,
        },
      })

      player2Ref.current = new (window as any).YT.Player('youtube-player-2', {
        height: '100%',
        width: '100%',
        videoId: videoId2,
        playerVars: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: onPlayer2Ready,
          onStateChange: onPlayer2StateChange,
        },
      })
    }

    return () => {
      if (player1Ref.current) {
        player1Ref.current.destroy()
      }
      if (player2Ref.current) {
        player2Ref.current.destroy()
      }
    }
  }, [videosLoaded, videoId1, videoId2])

  const onPlayer1Ready = () => {
    setPlayer1Ready(true)
  }

  const onPlayer1StateChange = (event: any) => {
    // YT.PlayerState.ENDED = 0
    if (event.data === 0) {
      setHasWatchedVideo1(true)
    }
  }

  const onPlayer2Ready = () => {
    setPlayer2Ready(true)
  }

  const onPlayer2StateChange = (event: any) => {
    // YT.PlayerState.ENDED = 0
    if (event.data === 0) {
      setHasWatchedVideo2(true)
    }
  }

  // Pause videos when switching steps
  useEffect(() => {
    if (currentStep === 'getting-started' && player2Ref.current?.pauseVideo) {
      player2Ref.current.pauseVideo()
    } else if (currentStep === 'video-tutorial' && player1Ref.current?.pauseVideo) {
      player1Ref.current.pauseVideo()
    }
  }, [currentStep])

  const handleCompleteOnboarding = async () => {
    if (!hasWatchedVideo2) {
      setError('Please watch the second video to the end before continuing')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await settingsStore.updateSettings({ onboarded: true })

      // Navigate to main page after successful onboarding
      navigate('/')
    } catch (err) {
      console.error('Error updating onboarding status:', err)
      setError('Failed to complete onboarding. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Layout withHeader={false}>
      {/* Custom header with only User button */}
      <header className="font-[Sora] fixed top-0 left-0 right-0 bg-gradient-to-r from-[#004D43] to-[#193133] shadow-lg z-20">
        <div className="mx-auto px-4 py-3">
          <div className="flex justify-between items-center text-teal-50">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-teal-50">
                <Logo fill="#f0fdfa" />
              </h1>
            </div>
            <div className="flex">
              <button onClick={() => navigate('/user')} className="p-2 rounded-full transition duration-200 hover:bg-[#E3FFAB33]" aria-label="User Profile">
                <User />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-screen flex-col items-center bg-gray-50 p-4 md:p-8">
        <div className="w-full max-w-4xl rounded-lg bg-white p-6 md:p-10 shadow-md">
          {/* Breadcrumb */}
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li className="flex items-center">
                <span onClick={() => setCurrentStep('getting-started')} className={`hover:text-primary cursor-pointer ${currentStep === 'getting-started' ? 'text-primary font-medium' : ''}`}>
                  Getting Started
                  {hasWatchedVideo1 && <span className="ml-1 text-green-600">✓</span>}
                </span>
              </li>
              <ChevronRight className="w-4 h-4" />
              <li className="flex items-center">
                <span
                  onClick={() => hasWatchedVideo1 && setCurrentStep('video-tutorial')}
                  className={`cursor-pointer ${currentStep === 'video-tutorial' ? 'text-primary font-medium' : ''} ${!hasWatchedVideo1 ? 'opacity-50 cursor-not-allowed' : 'hover:text-primary'}`}
                >
                  Video Tutorial
                  {hasWatchedVideo2 && <span className="ml-1 text-green-600">✓</span>}
                </span>
              </li>
            </ol>
          </nav>

          {/* Getting Started Step */}
          <div className={currentStep === 'getting-started' ? '' : 'hidden'}>
            <div className="mb-8 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4">Welcome to Georgia!</h1>
              <p className="text-base md:text-lg text-[#96BAFD] px-4 md:px-0">Watch the introduction video</p>
            </div>

            {/* First Video */}
            <div className="mb-6">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <div id="youtube-player-1" className="absolute inset-0 w-full h-full rounded-lg overflow-hidden bg-black flex items-center justify-center">
                  {!player1Ready && <Loader2 className="h-12 w-12 animate-spin text-white" />}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center space-y-4">
              <Button onClick={() => setCurrentStep('video-tutorial')} disabled={!hasWatchedVideo1} size="lg" variant={'success'} className="w-full max-w-xs">
                Next: Video Tutorial
              </Button>
              {!hasWatchedVideo1 && <p className="text-sm text-gray-500">Please watch the video to the end to continue</p>}
            </div>
          </div>

          {/* Video Tutorial Step */}
          <div className={currentStep === 'video-tutorial' ? '' : 'hidden'}>
            <div className="mb-8 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4">Video Tutorial</h1>
              <p className="text-base md:text-lg text-[#96BAFD] px-4 md:px-0">Watch this tutorial to complete onboarding</p>
            </div>

            {/* Second Video */}
            <div className="mb-8">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <div id="youtube-player-2" className="absolute inset-0 w-full h-full rounded-lg overflow-hidden bg-black flex items-center justify-center">
                  {!player2Ready && <Loader2 className="h-12 w-12 animate-spin text-white" />}
                </div>
              </div>
            </div>
          </div>

          {currentStep === 'video-tutorial' && (
            <>
              {error && (
                <div className="mb-6 rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex flex-col items-center space-y-4">
                <Button
                  onClick={handleCompleteOnboarding}
                  disabled={isLoading || !hasWatchedVideo2}
                  isLoading={isLoading}
                  loadingText="Processing..."
                  size="lg"
                  variant={'success'}
                  className="w-full max-w-xs"
                  leftIcon={<Smile className="w-5 h-5" />}
                >
                  Continue
                </Button>

                {!hasWatchedVideo2 && <p className="text-sm text-gray-500">Please watch the tutorial video to the end to continue</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  )
})

export default Welcome
